import {
  ButtonDefSchema,
  DoorDefSchema,
  type EntityDef,
  LiftDefSchema,
  SOLVER_MAX_GATES,
} from '@md/shared';
import type { ParsedLevel } from '../level/parser.js';

/**
 * Modèle des MÉCANISMES pour l'étage A du solveur (spec §8).
 *
 * Le moteur fournit trois entités génériques qui changent la topologie d'un
 * niveau : le **bouton** (signal maintenu par un robot), la **porte** (mur
 * tant que son signal est inactif) et l'**ascenseur** (plateforme qui monte
 * quand son signal est actif). Le solveur doit les connaître, sinon il propose
 * des plans que le rejeu ne peut pas exécuter — et refuse des niveaux
 * parfaitement jouables.
 *
 * Traduction retenue :
 *
 * | Entité | Modèle |
 * |---|---|
 * | `button` | un **signal** (bit) actif tant qu'un robot occupe une de ses tuiles |
 * | `lever` (et tout autre signal référencé) | un **verrou bistable** (bit d'état de recherche), actionné via `GameModule.solver.remoteSwitches` |
 * | `door` | ses tuiles portent le masque du signal : traversables SEULEMENT quand il est actif |
 * | `lift` (avec `linkedTo`) | deux plateformes (bas au repos, haut à l'arrivée) + une transition coopérative « se faire monter » |
 *
 * Ce que le solveur ne modélise PAS et considère donc comme absent : les blocs
 * poussés, les électroaimants du décor, les portes `inverted` (traitées comme
 * des murs pleins, choix conservateur) et les ascenseurs libres (sans
 * `linkedTo`), dont seules les plateformes d'extrémité sont connues.
 */

/** Un signal du niveau : bouton maintenu, ou verrou bistable. */
export interface SolverGate {
  readonly signalId: string;
  /** Bit du masque (`1 << index`). */
  readonly bit: number;
  /** `press` = maintenu par la position d'un robot ; `latch` = bistable. */
  readonly kind: 'press' | 'latch';
}

/** Ascenseur commandé : une plateforme basse, une haute, un signal. */
export interface SolverLift {
  readonly id: string;
  readonly signalBit: number;
  /** Tuiles où l'on se tient quand la plateforme est en bas. */
  readonly bottom: readonly TileRef[];
  /** Tuiles où l'on se tient quand la plateforme est en haut. */
  readonly top: readonly TileRef[];
}

export interface TileRef {
  readonly tx: number;
  readonly ty: number;
}

export interface SolverMechanisms {
  readonly gates: readonly SolverGate[];
  /** Masque des verrous bistables (ceux qui font partie de l'état de recherche). */
  readonly latchMask: number;
  /** Masque exigé pour traverser cette tuile (0 = libre). */
  gateMaskAt(tx: number, ty: number): number;
  /** La tuile est-elle rendue solide par une entité (porte inversée) ? */
  blockedAt(tx: number, ty: number): boolean;
  /** La tuile porte-t-elle une plateforme d'ascenseur (support supplémentaire) ? */
  platformAt(tx: number, ty: number): boolean;
  /** Signal exigé ACTIF pour que la plateforme soit à cette tuile (0 = aucun). */
  platformRequiresOn(tx: number, ty: number): number;
  /** Signal exigé INACTIF pour que la plateforme soit à cette tuile. */
  platformRequiresOff(tx: number, ty: number): number;
  /** Masque des signaux qu'un robot active en se tenant sur cette tuile. */
  pressMaskAt(tx: number, ty: number): number;
  readonly lifts: readonly SolverLift[];
  /** Bit d'un signal, ou 0 s'il est inconnu du modèle. */
  bitOf(signalId: string): number;
}

const EMPTY_MECHANISMS_LIFTS: readonly SolverLift[] = [];

function key(tx: number, ty: number): string {
  return `${tx},${ty}`;
}

/** Lecture tolérante : une entité mal formée est simplement ignorée ici. */
function safe<T>(parse: () => T): T | null {
  try {
    return parse();
  } catch {
    return null;
  }
}

/**
 * Construit le modèle des mécanismes d'un niveau.
 *
 * Aucune erreur n'en sort : le lint structurel (`lintLevel`) a déjà la charge
 * de signaler les entités invalides ou les `linkedTo` orphelins.
 */
export function buildMechanisms(level: ParsedLevel): SolverMechanisms {
  const defs: readonly EntityDef[] = level.entities;

  // --- 1. Signaux : boutons d'abord, puis tout `linkedTo` non résolu --------
  const bits = new Map<string, number>();
  const gates: SolverGate[] = [];
  const claim = (signalId: string, kind: SolverGate['kind']): number => {
    const existing = bits.get(signalId);
    if (existing !== undefined) {
      return existing;
    }
    if (gates.length >= SOLVER_MAX_GATES) {
      return 0;
    }
    const bit = 1 << gates.length;
    bits.set(signalId, bit);
    gates.push({ signalId, bit, kind });
    return bit;
  };

  const pressTiles = new Map<string, number>();
  for (const def of defs) {
    if (def.type !== 'button') {
      continue;
    }
    const cfg = safe(() => ButtonDefSchema.parse(def));
    if (!cfg) {
      continue;
    }
    const bit = claim(cfg.id, 'press');
    for (let i = 0; i < Math.ceil(cfg.width); i += 1) {
      const tile = key(cfg.x + i, cfg.y);
      pressTiles.set(tile, (pressTiles.get(tile) ?? 0) | bit);
    }
  }

  // Tout signal référencé mais non produit par un bouton est un bistable :
  // c'est le GameModule qui déclarera comment on l'actionne.
  for (const def of defs) {
    const linked = def.linkedTo;
    if (typeof linked === 'string') {
      claim(linked, 'latch');
    }
  }

  // --- 2. Consommateurs : portes et ascenseurs -----------------------------
  const gateTiles = new Map<string, number>();
  const blocked = new Set<string>();
  const platforms = new Set<string>();
  const platformOn = new Map<string, number>();
  const platformOff = new Map<string, number>();
  const lifts: SolverLift[] = [];

  for (const def of defs) {
    if (def.type === 'door') {
      const cfg = safe(() => DoorDefSchema.parse(def));
      if (!cfg) {
        continue;
      }
      const bit = bits.get(cfg.linkedTo) ?? 0;
      for (let dy = 0; dy < Math.ceil(cfg.height); dy += 1) {
        for (let dx = 0; dx < Math.ceil(cfg.width); dx += 1) {
          const tile = key(cfg.x + dx, cfg.y + dy);
          // Porte inversée (ouverte au repos, fermée par le signal) : le
          // solveur ne sait pas la refermer à bon escient, il la traite donc
          // comme un mur plein — conservateur, jamais optimiste.
          if (cfg.inverted || bit === 0) {
            blocked.add(tile);
          } else {
            gateTiles.set(tile, (gateTiles.get(tile) ?? 0) | bit);
          }
        }
      }
      continue;
    }

    if (def.type === 'lift') {
      const cfg = safe(() => LiftDefSchema.parse(def));
      if (!cfg) {
        continue;
      }
      const first = cfg.path[0];
      const last = cfg.path[cfg.path.length - 1];
      if (!first || !last) {
        continue;
      }
      const width = Math.ceil(cfg.width);
      const bottom: TileRef[] = [];
      const top: TileRef[] = [];
      for (let i = 0; i < width; i += 1) {
        bottom.push({ tx: first[0] + i, ty: first[1] });
        top.push({ tx: last[0] + i, ty: last[1] });
      }
      // La plateforme est un SUPPORT : on se tient sur la tuile juste
      // au-dessus d'elle, aux deux extrémités du chemin.
      for (const ref of [...bottom, ...top]) {
        platforms.add(key(ref.tx, ref.ty));
      }
      const bit = cfg.linkedTo === undefined ? 0 : (bits.get(cfg.linkedTo) ?? 0);
      if (bit !== 0 && (last[1] !== first[1] || last[0] !== first[0])) {
        lifts.push({ id: cfg.id, signalBit: bit, bottom, top });
        // La plateforme n'est en bas que signal ÉTEINT, en haut que signal
        // ACTIF : c'est ce qui interdit au solveur de « prendre » un ascenseur
        // déjà parti.
        for (const ref of bottom) {
          const tile = key(ref.tx, ref.ty);
          platformOff.set(tile, (platformOff.get(tile) ?? 0) | bit);
        }
        for (const ref of top) {
          const tile = key(ref.tx, ref.ty);
          platformOn.set(tile, (platformOn.get(tile) ?? 0) | bit);
        }
      }
    }
  }

  const latchMask = gates
    .filter((gate) => gate.kind === 'latch')
    .reduce((mask, gate) => mask | gate.bit, 0);

  return {
    gates,
    latchMask,
    lifts: lifts.length === 0 ? EMPTY_MECHANISMS_LIFTS : lifts,
    gateMaskAt: (tx, ty) => gateTiles.get(key(tx, ty)) ?? 0,
    blockedAt: (tx, ty) => blocked.has(key(tx, ty)),
    platformAt: (tx, ty) => platforms.has(key(tx, ty)),
    platformRequiresOn: (tx, ty) => platformOn.get(key(tx, ty)) ?? 0,
    platformRequiresOff: (tx, ty) => platformOff.get(key(tx, ty)) ?? 0,
    pressMaskAt: (tx, ty) => pressTiles.get(key(tx, ty)) ?? 0,
    bitOf: (signalId) => bits.get(signalId) ?? 0,
  };
}

/** Modèle vide : aucun mécanisme (utilisé par défaut et par les tests). */
export function noMechanisms(): SolverMechanisms {
  return {
    gates: [],
    latchMask: 0,
    lifts: EMPTY_MECHANISMS_LIFTS,
    gateMaskAt: () => 0,
    blockedAt: () => false,
    platformAt: () => false,
    platformRequiresOn: () => 0,
    platformRequiresOff: () => 0,
    pressMaskAt: () => 0,
    bitOf: () => 0,
  };
}
